import { ChevronLeft, X } from 'lucide-react'
import type { MDXMetadata } from 'mdx/types'
import { Link, useLocation } from '#router'
import { cn } from '@/lib/utils'
import { Button } from './ui/button'

function NavItem({ page, depth = 1 }: { page: MDXMetadata; depth?: number }) {
  const location = useLocation()
  const isActive = page.href === location.page?.href

  return (
    <li>
      <Link
        className={cn(
          'hover:bg-sidebar-accent text-sidebar-foreground/80 mx-2 block rounded-md px-2 py-2 text-sm leading-tight transition-colors',
          isActive &&
            'bg-sidebar-accent/70 text-sidebar-foreground font-semibold',
        )}
        href={page.href}
      >
        {page.title}
      </Link>
      {page.children?.length ? (
        <ul className="border-l" style={{ marginLeft: `${depth}rem` }}>
          {page.children.map((subpage) => (
            <NavItem key={subpage.href} page={subpage} depth={depth + 1} />
          ))}
        </ul>
      ) : null}
    </li>
  )
}

export default function Sidebar({
  className,
  open,
  onClose,
  ...props
}: React.ComponentProps<'aside'> & { open: boolean; onClose: () => void }) {
  const { course, page } = useLocation()
  if (!course || !page) return null

  return (
    <aside
      data-open={open}
      className={cn(
        'course-sidebar bg-sidebar flex min-h-[calc(100vh-4rem-1px)] w-68 flex-col border-r',
        className,
      )}
      {...props}
    >
      <div className="sticky top-0">
        <div className="flex flex-none flex-col px-4 pt-2">
          <div className="mb-8 flex gap-2">
            <Button
              className="text-sidebar-foreground/70 flex-auto bg-none text-xs shadow-none"
              variant="outline"
              size="sm"
              asChild
            >
              <Link href="/courses">
                <ChevronLeft />
                Back to Courses
              </Link>
            </Button>
            <Button
              className="sidebar-close-button text-sidebar-foreground/70 -mr-2 flex-none text-xs"
              variant="ghost"
              size="icon-sm"
              title="Close Sidebar"
              onClick={onClose}
            >
              <X />
            </Button>
          </div>
          <div className="text-sidebar-foreground/60 mb-2 text-xs font-semibold">
            Course
          </div>
          <div className="mb-8 flex flex-auto flex-col">
            <h2 className="text-sidebar-foreground/90 text-xs font-semibold">
              {course.title}
            </h2>
            <p className="text-sidebar-foreground/70 text-xs">
              {course.subtitle}
            </p>
          </div>
          <div className="text-sidebar-foreground/60 text-xs font-semibold">
            Course Content
          </div>
        </div>
        <div className="relative flex flex-auto flex-col">
          <div className="from-sidebar pointer-events-none absolute top-0 right-0 left-0 z-10 h-4 bg-gradient-to-b to-transparent" />
          <ul className="flex h-[calc(100vh-262px)] flex-col gap-0.5 overflow-auto py-2">
            <li>
              <Link
                className={cn(
                  'hover:bg-sidebar-accent text-sidebar-foreground/80 mx-2 block rounded-md px-2 py-2 text-sm leading-tight transition-colors',
                  page.href === course.href &&
                    'bg-sidebar-accent/70 text-sidebar-foreground font-semibold',
                )}
                href={course.href}
              >
                Course Overview
              </Link>
            </li>
            {course.children?.map((subpage) => (
              <NavItem key={subpage.href} page={subpage} />
            ))}
          </ul>
          <div className="border-t">
            <div className="text-sidebar-foreground/60 flex flex-col items-start gap-1 p-4 text-xs">
              <div>
                Made with <span className="text-pink-500">♥</span> by Mike
                Mroczka
              </div>
              <div>©{new Date().getFullYear()} All rights reserved</div>
            </div>
          </div>
        </div>
      </div>
    </aside>
  )
}
